import React from 'react';
import { useTranslation } from 'react-i18next';
import { FilterMinimalStyleCircle16 } from 'shared/assets/icons';
import {
  Button,
  Colors as ButtonColors,
  Sizes,
  Variants,
} from 'shared/components/ui/button';
import { FilterContextMenu } from 'shared/components/ui/filter-context-menu';
import {
  Colors as IconButtonColors,
  IconButton,
} from 'shared/components/ui/icon-button';
import { useFilterState } from 'shared/hooks/use-filter-state';
import { useOnFilterOption } from 'shared/hooks/use-on-filter';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { useUser } from 'shared/hooks/use-user';
import { initialDateFormat } from 'shared/types/date-format-options';
import { FilterDocument, FilterOptionKeys } from 'shared/types/filter-document';
import { Goal } from 'shared/types/goal';
import { LifeArea } from 'shared/types/life-area';
import { GroupByMode, SortingMode } from 'shared/types/sorting-mode';
import { Tag } from 'shared/types/tag';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';

import * as Styled from './filter-button.style';

export type FilterButtonProps<
  GBM extends GroupByMode,
  SM extends SortingMode,
  F extends FilterOptionKeys,
> = {
  filterDocument: FilterDocument<GBM, SM, F>;
  defaultFilterDocument?: FilterDocument<GBM, SM, F>;
  supportedGroupByModes?: GBM[];
  supportedSortingModes?: SM[];
  supportedFilters?: F[];
  goals: Goal[];
  lifeAreas: LifeArea[];
  tags: Tag[];
  updateFilterDocument: (filterDocument: FilterDocument<GBM, SM, F>) => void;
  onReset: () => void;
  labelOverrides?: Partial<Record<GroupByMode, string>>;
  hideNoGoal?: boolean;
  renderAsIconButton?: boolean;
  preventDrag?: boolean;
};

export const FilterButton = <
  GBM extends GroupByMode,
  SM extends SortingMode,
  F extends FilterOptionKeys,
>({
  filterDocument,
  defaultFilterDocument,
  supportedGroupByModes,
  supportedSortingModes,
  supportedFilters,
  goals,
  lifeAreas,
  tags,
  updateFilterDocument,
  onReset,
  labelOverrides,
  hideNoGoal,
  renderAsIconButton,
  preventDrag,
}: FilterButtonProps<GBM, SM, F>) => {
  const filterButtonRef = React.useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const user = useUser();
  const dateFormat = user?.settings?.dateFormat ?? initialDateFormat;
  const weekStartsOn = getWeekStartsOn(user);
  const {
    menuOpen: filterMenuOpen,
    openMenu: openFilterMenu,
    closeMenu: closeFilterMenu,
  } = useOpenMenu();

  const { filters, activeFilters, activeOptions } = useFilterState(
    filterDocument,
    {
      lifeAreas,
      tags,
      goals,
      dateFormat,
      weekStartsOn,
      defaultFilterDocument,
      supportedGroupByModes,
      supportedSortingModes,
      supportedFilters,
      labelOverrides,
      hideNoGoal,
    },
  );

  const onFilter = useOnFilterOption(
    filterDocument,
    defaultFilterDocument,
    updateFilterDocument,
  );

  const filterButtonLabel = activeFilters
    ? `${t('general.labels.filter')}: ${activeFilters}`
    : t('general.labels.filter');

  return (
    <>
      {renderAsIconButton ? (
        <Styled.IconButtonContainer $isActive={!!activeFilters}>
          <IconButton
            ref={filterButtonRef}
            icon={FilterMinimalStyleCircle16}
            onClick={openFilterMenu}
            highlighted={filterMenuOpen}
            color={
              activeFilters
                ? IconButtonColors.Inherit
                : IconButtonColors.Default
            }
            preventDrag={preventDrag}
          />
          <Styled.Badge $hidden={!activeFilters}>{activeFilters}</Styled.Badge>
        </Styled.IconButtonContainer>
      ) : (
        <Button
          ref={filterButtonRef}
          onClick={openFilterMenu}
          endIcon={FilterMinimalStyleCircle16}
          size={Sizes.Small}
          variant={Variants.Text}
          color={activeFilters ? ButtonColors.Primary : ButtonColors.Inherit}
          selected={!!activeFilters}
          preventDrag={preventDrag}
        >
          {filterButtonLabel}
        </Button>
      )}

      {filterMenuOpen && (
        <FilterContextMenu
          referenceElement={filterButtonRef}
          onOutside={closeFilterMenu}
          position="bottom-end"
          title={t('context-menus.filtering.task.title')}
          options={filters}
          activeOptions={activeOptions}
          onOption={onFilter}
          onReset={onReset}
        />
      )}
    </>
  );
};
